/**
 * One link on the linkage canvas and its gate (Feature 72, 4.1, 4.4): a
 * stepped line from a step to the next, with a gate marker at its middle.
 * On the editor's canvas the marker says what decides the next step: "auto",
 * how many conditions, or "empty", which never passes and which a save
 * refuses; a link whose gate is empty is drawn dashed in the warning colour,
 * and a save's problem on it turns it red. On a run's canvas, once the step
 * is decided, the marker says what the gate decided instead, and a gate that
 * counted list tests opens into a card of its conditions, the list tests with
 * their counts and, on a blocked gate, the one that fell short in the warning
 * colour (Feature 77 D24). The marker is a label, not a control: the whole
 * edge selects the link. On the editor's canvas, resting the pointer on the
 * link, or the keyboard on it, opens a card of the gate's conditions in full.
 */
import type { LinkageGateOutcome } from '@optum/api-client/types';

import { HoverCard } from '../../ui/HoverCard.js';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  type Edge,
  type EdgeProps,
} from '../../ui/flow.js';
import type { FlowEdge } from './draft.js';
import { NODE_WIDTH } from './draft.js';
import { COLUMN } from './layout.js';
import { conditionLines, gateLines } from './gate-lines.js';
import { gateSummary, gateText, outcomeText } from './gates.js';
import { useHovered } from './hovered.js';

export type RulesEdgeData = FlowEdge['data'] & Record<string, unknown>;
export type RulesEdgeType = Edge<RulesEdgeData, 'rules'>;

interface Look {
  stroke: string;
  ink: string;
  fill: string;
  border: string;
  /** The diamond's colour. */
  mark: string;
  dashed: boolean;
}

const QUIET: Look = {
  stroke: 'var(--border-hover)',
  ink: 'var(--text-muted)',
  fill: 'var(--surface-card)',
  border: 'var(--border-input)',
  mark: 'var(--text-muted)',
  dashed: false,
};

function toned(tone: 'ok' | 'warn' | 'danger' | 'blue', dashed = false): Look {
  return {
    stroke: `var(--${tone})`,
    ink: `var(--${tone})`,
    fill: `var(--${tone}-tint)`,
    border: `var(--${tone})`,
    mark: `var(--${tone})`,
    dashed,
  };
}

// What a run's gate decided, as the RunGates artboard draws it.
const NOT_REACHED: Look = {
  stroke: 'var(--border-hairline)',
  ink: 'var(--text-faint)',
  fill: 'var(--surface-card)',
  border: 'var(--border-hairline)',
  mark: 'var(--border-hover)',
  dashed: true,
};

const OUTCOME_LOOK: Record<LinkageGateOutcome, Look> = {
  passed: toned('ok'),
  auto: { ...QUIET, mark: 'var(--text-faint)' },
  blocked: toned('warn'),
  empty: toned('danger', true),
  not_reached: NOT_REACHED,
  // A branch its decision did not take reads as a link the run never reached (Feature 79 D23).
  not_taken: NOT_REACHED,
};

/** How the link and its marker are drawn: a problem, an outcome, the gate, the selection. */
function lookOf(data: RulesEdgeData | undefined, selected: boolean): Look {
  if ((data?.problems.length ?? 0) > 0) return toned('danger', data?.gate.kind === 'empty');
  if (data?.outcome !== undefined) return OUTCOME_LOOK[data.outcome];
  if (data?.gate.kind === 'empty') return toned('warn', true);
  if (selected) return toned('blue');
  // A branch from a call is drawn in the call's color up to its step (Feature 78 D8).
  return data?.branch ? { ...QUIET, stroke: 'var(--info)' } : QUIET;
}

/** The marker's words: the outcome once decided, else the gate. */
function markerText(data: RulesEdgeData): string {
  return data.outcome !== undefined ? outcomeText(data.outcome) : gateText(data.gate);
}

/** The gate's hover card: the link, what decides it, and each condition in full. */
function GateCard({ from, to, data }: { from: string; to: string; data: RulesEdgeData }) {
  const lines = data.gate.kind === 'conditions' ? conditionLines(data.rules) : [];
  return (
    <>
      <span style={{ fontWeight: 'var(--weight-semibold)' }}>
        <span className="mono">
          {from} → {to}
        </span>{' '}
        · {gateSummary(data.gate, data.rules.join)}
      </span>
      {lines.map((line) => (
        <span key={line} className="mono" style={{ fontSize: 'var(--text-meta)' }}>
          {line}
        </span>
      ))}
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--text-faint)' }}>
        Click the link to edit its gate in the inspector
      </span>
    </>
  );
}

export function RulesEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps<RulesEdgeType>) {
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 7,
  });
  const look = lookOf(data, Boolean(selected));
  const decided = data?.outcome === 'passed' || data?.outcome === 'blocked';
  const lines = data && decided && data.lists?.length ? gateLines(data.rules, data.lists) : [];
  const hovered = useHovered(id);
  return (
    <>
      <BaseEdge
        path={path}
        interactionWidth={18}
        style={{
          stroke: look.stroke,
          strokeWidth: 2,
          strokeDasharray: look.dashed ? '5 5' : undefined,
        }}
      />
      {/* A link out of a decision has no gate: its branch is named on the decision. */}
      {data && data.gate.kind !== 'branch' && (
        <EdgeLabelRenderer>
          <HoverCard open={hovered} content={<GateCard from={source} to={target} data={data} />}>
            <div
              data-tour="gate"
              style={{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                padding: lines.length > 0 ? '5px 9px' : '2px 9px 2px 7px',
                borderRadius: lines.length > 0 ? 'var(--radius-control)' : 'var(--radius-pill)',
                fontSize: 'var(--text-meta-lg)',
                fontWeight: 'var(--weight-semibold)',
                background: look.fill,
                color: look.ink,
                border: `1px solid ${look.border}`,
                whiteSpace: 'nowrap',
                // A card fits between two columns of nodes, its lines wrapping.
                ...(lines.length > 0 ? { maxWidth: COLUMN - NODE_WIDTH - 10 } : {}),
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 7,
                    height: 7,
                    transform: 'rotate(45deg)',
                    borderRadius: 2,
                    background: look.mark,
                  }}
                />
                {markerText(data)}
              </span>
              {lines.map((line) => (
                <span
                  key={line.text}
                  className="mono"
                  style={{
                    whiteSpace: 'normal',
                    fontSize: 'var(--text-meta)',
                    lineHeight: 1.35,
                    fontWeight: 'var(--weight-regular)',
                    color:
                      data.outcome === 'blocked' && line.short
                        ? 'var(--warn)'
                        : 'var(--text-muted)',
                  }}
                >
                  {line.text}
                </span>
              ))}
            </div>
          </HoverCard>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
